'use client';

import React, { Suspense, useState } from 'react';
import { Canvas, useLoader, useThree, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';

const PanoramaSphere = ({ imageUrl }) => {
    const texture = useLoader(THREE.TextureLoader, imageUrl);

    // Coordinate finder: click anywhere in the 360 view to log the exact [x, y, z] coordinates
    const handleClick = (e) => {
        console.log("Clicked Coordinates [x, y, z]:", [e.point.x, e.point.y, e.point.z]);
    };

    return (
        <mesh scale={[-1, 1, 1]} rotation={[0, -Math.PI / 2, 0]} onClick={handleClick}>
            <sphereGeometry args={[500, 60, 40]} />
            <meshBasicMaterial map={texture} side={THREE.BackSide} />
        </mesh>
    );
};

const ZoomController = ({ zoomLevel }) => {
    const { camera } = useThree();

    React.useEffect(() => {
        camera.fov = zoomLevel;
        camera.updateProjectionMatrix();
    }, [zoomLevel, camera]);

    return null;
};



export default function InteriorView({ car }) {
    const [modalInfo, setModalInfo] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(75); // Default FOV is 75

    if (!car) return null;

    const panoramaImage = car.interior?.image_6;

    // We define hotspots here. When the image is fetched from the database, 
    // the database should also provide these coordinates for that specific car.
    // I put example coordinates here, but we will fine-tune them using the console log trick!
    const hotspots = [
        {
            id: 'steering-wheel',
            position: [-100, -50, -200], // Example [x, y, z] coordinates
            title: 'فرمان',
            description: 'این فرمان اسپرت با روکش چرم طبیعی طراحی شده است.',
        }
    ];

    return (
        <div className="w-full flex flex-col items-center">
            {panoramaImage ? (
                <div className="w-full h-[60vh] md:h-[70vh] max-h-[800px] relative rounded-2xl overflow-hidden cursor-grab active:cursor-grabbing shadow-inner">
                    <Canvas camera={{ position: [0, 0, 0.1] }}>
                        <OrbitControls
                            enableZoom={false} // Disabled default zoom so we don't move the camera out of the center
                            enablePan={false}
                            rotateSpeed={-0.5}
                        />
                        <ZoomController zoomLevel={zoomLevel} />

                        <Suspense fallback={null}>
                            <PanoramaSphere imageUrl={panoramaImage} />
                        </Suspense>

                    </Canvas>


                    {/* Zoom Buttons Overlay - Premium Pill Design */}
                    <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center bg-white/70 backdrop-blur-md rounded-full shadow-lg p-1 z-10 border border-white/50">
                        <button
                            onClick={() => setZoomLevel((prev) => Math.min(100, prev + 10))}
                            className="w-10 h-10 md:w-12 md:h-12 hover:bg-white/60 text-gray-800 rounded-full flex items-center justify-center text-xl transition-all"
                            title="Zoom Out"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                        </button>
                        <div className="w-px h-6 bg-gray-400/30 mx-1"></div>
                        <button
                            onClick={() => setZoomLevel((prev) => Math.max(30, prev - 10))}
                            className="w-10 h-10 md:w-12 md:h-12 hover:bg-white/60 text-gray-800 rounded-full flex items-center justify-center text-xl transition-all"
                            title="Zoom In"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 md:h-6 md:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    </div>

                    {/* Simple UI Modal floating over the Canvas */}
                    {modalInfo && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 backdrop-blur-sm" dir="rtl">
                            <div className="bg-white p-6 rounded-2xl max-w-sm w-full mx-4 shadow-2xl transform transition-all">
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{modalInfo.title}</h3>
                                <p className="text-gray-600 mb-6">{modalInfo.description}</p>
                                <button
                                    onClick={() => setModalInfo(null)}
                                    className="w-full py-2 bg-gray-900 hover:bg-black text-white rounded-lg transition-colors font-medium"
                                >
                                    بستن
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="w-full text-center py-20 text-gray-400 border border-dashed border-gray-200 rounded-xl" dir="rtl">
                    تصویری برای نمای داخلی این خودرو ثبت نشده است
                </div>
            )}
        </div>
    );
}